import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { AppThemeProvider } from './lib/theme';
import { ToastProvider } from './components/ds-dlp';
import { DialogProvider } from './components/dialogs/DialogProvider';
import { ToastBridge } from './lib/toast';
// The d2d faces (cascade plan 3.4), bundled with the app. index.css names them:
// --font-inter-face, --font-geist-mono, --font-plus-jakarta.
import '@fontsource-variable/inter';
import '@fontsource-variable/geist-mono';
import '@fontsource/plus-jakarta-sans/500.css';
// The d2d design system: Tailwind v4, tokens, ds/ styles (cascade plan 2.3).
import './index.css';

// /__ds, the design-system gallery (cascade plan 2.7). `import.meta.env.DEV` is
// a build-time constant, so a production build drops both the branch and the
// lazy chunk: /__ds there is the app's own unknown-route page. It mounts here,
// outside App, because App renders nothing but Login until a user signs in.
const isGallery = import.meta.env.DEV && window.location.pathname.replace(/\/$/, '') === '/__ds';
const DsGallery = isGallery ? lazy(() => import('./pages/DsGallery')) : null;

// ─── QueryClient + Root ────────────────────────────────────────────────────────
const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false, retry: 1 } },
});

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
// AppThemeProvider (lib/theme.tsx, d2d-XDR's) owns the theme (cascade plan
// 3.1): it follows the OS by default, keeps an explicit choice under
// "d2d-theme", and writes data-ds-mode, which index.html's pre-paint script has
// already set for the first frame. One ToastProvider (lib/toast's toast.* calls
// reach it through ToastBridge) and one DialogProvider serve the whole app.
root.render(
  <React.StrictMode>
    <AppThemeProvider>
      <ToastProvider>
        <ToastBridge />
        <DialogProvider>
          {DsGallery ? (
            <Suspense fallback={null}>
              <DsGallery />
            </Suspense>
          ) : (
            <QueryClientProvider client={queryClient}>
              <App />
            </QueryClientProvider>
          )}
        </DialogProvider>
      </ToastProvider>
    </AppThemeProvider>
  </React.StrictMode>
);
